import { ref } from "vue";
import {
  searchGeographyRegions,
  searchGeographyMetroAreas,
  searchGeographyLocalUnits,
  resolveGeographyRefs,
} from "@/api/geographyAPI";

function mapRegionRow(row) {
  return {
    id: row.id,
    name: row.name,
    code: row.code,
  };
}

function mapSubRegionRow(row) {
  const item = {
    id: row.id,
    name: row.display_label || row.name,
  };
  const parentRegionId = row.parent_region_id ?? row.parentRegionId;
  if (parentRegionId != null) {
    item.parentRegionId = parentRegionId;
  }
  return item;
}

function subRegionRef(location) {
  if (!location?.subRegionId) return null;
  const isCounty =
    location.scope === "county" || location.subRegionType === "county";
  return `${isCounty ? "local_unit" : "metro_area"}:${location.subRegionId}`;
}

function refsForLocation(location) {
  if (!location) return [];
  const refs = [];
  if (location.regionId != null) refs.push(`region:${location.regionId}`);
  const subRef = subRegionRef(location);
  if (subRef) refs.push(subRef);
  return refs;
}

function needsParentRegionId(location) {
  if (!location?.subRegionId || location.regionId != null) return false;
  const scope = location.scope || location.subRegionType;
  return scope === "metro" || scope === "county";
}

function applyResolveResults(location, results = []) {
  if (!location || !results.length) return location;
  const next = { ...location };
  for (const row of results) {
    if (row.type === "region") {
      next.regionId = row.id;
      next.regionName = row.display_label || row.name;
      if (row.code) next.regionCode = row.code;
    } else if (row.type === "metro_area" || row.type === "local_unit") {
      next.subRegionName = row.display_label || row.name;
      if (next.regionId == null && row.parent_region_id != null) {
        next.regionId = row.parent_region_id;
      }
    }
  }
  return next;
}

/**
 * Geography type-to-search for LocationPicker in the Pay Range wizard (ER-11920).
 *
 * @param {{ getLocation: () => object|null, setLocation: (loc: object|null) => void }} ctx
 */
export function usePayRangeLocationSearch({ getLocation, setLocation }) {
  const regionItems = ref([]);
  const subRegionItems = ref([]);
  const locationSearchLoading = ref(false);

  let searchSeq = 0;

  function resetSearchResults() {
    regionItems.value = [];
    subRegionItems.value = [];
    searchSeq += 1;
    locationSearchLoading.value = false;
  }

  async function enrichLocationParentRegion(location) {
    if (!needsParentRegionId(location)) return location;
    const subRef = subRegionRef(location);
    if (!subRef) return location;

    try {
      const { data } = await resolveGeographyRefs([subRef]);
      let next = applyResolveResults({ ...location }, data?.results || []);
      if (next.regionId != null && !next.regionName) {
        const { data: regionData } = await resolveGeographyRefs([
          `region:${next.regionId}`,
        ]);
        next = applyResolveResults(next, regionData?.results || []);
      }
      return next;
    } catch {
      return location;
    }
  }

  async function hydrateLocationFromRefs(location) {
    if (!location) return;
    const needsRegionLabel = location.regionId != null && !location.regionName;
    const needsSubLabel = location.subRegionId != null && !location.subRegionName;
    const needsParent = needsParentRegionId(location);
    if (!needsRegionLabel && !needsSubLabel && !needsParent) return;

    try {
      let working = location;
      if (needsParent) {
        working = await enrichLocationParentRegion(working);
      }
      const { data } = await resolveGeographyRefs(refsForLocation(working));
      const enriched = applyResolveResults(working, data?.results || []);
      setLocation(enriched);
    } catch {
      /* keep ids; labels may stay blank until user searches */
    }
  }

  /** Fill parent region_id (and labels) before suggest / create-draft API calls. */
  async function ensureLocationForSuggest() {
    const location = getLocation();
    if (!location) return;
    await hydrateLocationFromRefs(location);
    const current = getLocation();
    if (!needsParentRegionId(current)) return;
    const enriched = await enrichLocationParentRegion(current);
    if (enriched !== current) {
      setLocation(enriched);
      await hydrateLocationFromRefs(enriched);
    }
  }

  async function handleLocationSearch(payload) {
    const { level, query, countryCode: payloadCountry, regionCode: payloadRegion } =
      payload || {};
    if (level === "country") return;

    const location = getLocation();
    const country = location?.countryCode || payloadCountry;
    if (!country) {
      resetSearchResults();
      return;
    }

    const seq = ++searchSeq;
    locationSearchLoading.value = true;

    try {
      if (level === "region") {
        subRegionItems.value = [];
        const { data } = await searchGeographyRegions(country, query);
        if (seq !== searchSeq) return;
        regionItems.value = (data || []).map(mapRegionRow);
        return;
      }

      if (level === "subregion") {
        const scope = location?.scope || location?.subRegionType;
        const regionCode = location?.regionCode || payloadRegion;
        const params = { country, q: query ?? "" };
        if (regionCode) params.region = regionCode;

        const fetcher =
          scope === "county" ? searchGeographyLocalUnits : searchGeographyMetroAreas;

        const { data } = await fetcher(params);
        if (seq !== searchSeq) return;
        subRegionItems.value = (data || []).map(mapSubRegionRow);
      }
    } catch {
      if (seq !== searchSeq) return;
      if (level === "region") regionItems.value = [];
      if (level === "subregion") subRegionItems.value = [];
    } finally {
      if (seq === searchSeq) locationSearchLoading.value = false;
    }
  }

  return {
    regionItems,
    subRegionItems,
    locationSearchLoading,
    handleLocationSearch,
    hydrateLocationFromRefs,
    ensureLocationForSuggest,
    resetSearchResults,
  };
}
